import { trackError, trackEvent } from '@/helpers/tracking.helpers';
import { SocketEvents, TrackingEvents } from '@/helpers/enum';
import {
    DEFAULT_NOTIFICATIONS_PING_INTERVAL,
    NOTIFICATION_SOCKET_LOGGING_ENABLED,
    DEFAULT_ACK_TIMEOUT
} from "@/helpers/constants";
import utilService from '@/services/util.service';

type EventHandler<T = any> = (...args: T[]) => void;
type AckCallback<T = any> = (response?: T, error?: Error) => void;

class NotificationSocketBuilder {
    private static instance: NotificationSocketBuilder | null = null;
    private socket: WebSocket | null = null;
    private eventHandlers: Map<string, EventHandler[]> = new Map();
    private enableLogging = NOTIFICATION_SOCKET_LOGGING_ENABLED;
    private SOCKET_TAG = "NotificationSocketBuilder :";
    private isConnected = false;
    private isConnecting = false;
    private pingInterval: NodeJS.Timeout | null = null;
    private lastPongReceived: number = 0;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private messageQueue: Array<{ event: string, data: any, callback?: AckCallback }> = [];
    private pendingRequests: Map<string, { resolve: Function, reject: Function, timeout: NodeJS.Timeout }> = new Map();
    private requestId = 0;


    private currentUserId: string | null = null; // Track current user
    private isInitialized = false; // Track initialization state

    private connectionResolve: ((value: void | PromiseLike<void>) => void) | null = null;
    private connectionReject: ((reason?: any) => void) | null = null;
    private connectionTimeout: NodeJS.Timeout | null = null;

    // Private constructor for singleton
    private constructor() { }

    static getInstance(): NotificationSocketBuilder {
        if (!NotificationSocketBuilder.instance) {
            NotificationSocketBuilder.instance = new NotificationSocketBuilder();
        }
        return NotificationSocketBuilder.instance;
    }

    // Initialize with user ID
    // initialize(userId: string): this {
    //     if (this.socket) {
    //         this.cleanup();
    //     }

    //     const socketUrl = `${utilService.getLiveHostURL()}/nsweb?userId=${userId}`;
    //     console.log("Notification Socket URL:", socketUrl);

    //     // const socketUrl = `wss://qa-test.traderverse.io/notifications?userId=${userId}`;


    //     if (this.enableLogging) {
    //         console.log(`${this.SOCKET_TAG} Initializing for user ${userId}`);
    //         console.log(`${this.SOCKET_TAG} Connecting to: ${socketUrl}`);
    //     }

    //     this.setupWebSocket(socketUrl, userId);
    //     return this;
    // }
    initialize(userId: string): this {
        // If already initialized with the same user, do nothing
        if (this.isInitialized && this.currentUserId === userId && this.isConnected) {
            if (this.enableLogging) {
                console.log(`${this.SOCKET_TAG} Already initialized for user ${userId}`);
            }
            return this;
        }

        // If initialized with different user or not connected, cleanup first
        if (this.socket) {
            this.cleanup();
        }

        this.currentUserId = userId;
        this.isInitialized = true;

        const socketUrl = `${utilService.getLiveHostURL()}/nsweb?userId=${userId}`;
        if (this.enableLogging) {
            console.log(`${this.SOCKET_TAG} Initializing for user ${userId}`);
        }

        this.setupWebSocket(socketUrl, userId);
        return this;
    }
    private setupWebSocket(url: string, userId: string): void {

        if (this.isConnecting) {
            if (this.enableLogging) {
                console.log(`${this.SOCKET_TAG} Already connecting, skipping...`);
            }
            return;
        }
        try {
            this.isConnecting = true;
            this.socket = new WebSocket(url);

            this.socket.onopen = () => {
                this.isConnecting = false;
                this.isConnected = true;
                this.reconnectAttempts = 0;

                if (this.enableLogging) {
                    console.log(`${this.SOCKET_TAG} WebSocket connected`);
                }

                // Resolve the connection promise
                if (this.connectionResolve) {
                    this.connectionResolve();
                    this.connectionResolve = null;
                    this.connectionReject = null;
                }
                if (this.connectionTimeout) {
                    clearTimeout(this.connectionTimeout);
                    this.connectionTimeout = null;
                }

                trackSocketEvent(TrackingEvents.socketConnected, {
                    context: 'notifications',
                    url
                });

                this.sendJoinRoom(userId);
                this.setupHealthChecks();
            };

            this.socket.onclose = (event) => {
                this.isConnected = false;
                const reason = event.code === 1000 ? 'Normal closure' : `Code: ${event.code}, Reason: ${event.reason}`;

                if (this.enableLogging) {
                    console.log(`${this.SOCKET_TAG} WebSocket disconnected:`, reason);
                }

                // Reject the connection promise if it exists
                if (this.connectionReject) {
                    this.connectionReject(new Error(`Connection closed: ${reason}`));
                    this.connectionResolve = null;
                    this.connectionReject = null;
                }
                if (this.connectionTimeout) {
                    clearTimeout(this.connectionTimeout);
                    this.connectionTimeout = null;
                }

                trackSocketEvent(TrackingEvents.socketDisconnected, {
                    context: 'notifications',
                    reason
                });

                this.handleReconnection(userId);
            };

            this.socket.onerror = (error) => {
                this.isConnecting = false;
                if (this.enableLogging) {
                    console.error(`${this.SOCKET_TAG} WebSocket error:`, error);
                }

                // Reject the connection promise if it exists
                if (this.connectionReject) {
                    this.connectionReject(error);
                    this.connectionResolve = null;
                    this.connectionReject = null;
                }
                if (this.connectionTimeout) {
                    clearTimeout(this.connectionTimeout);
                    this.connectionTimeout = null;
                }

                trackSocketError(error, TrackingEvents.socketConnectionError, { context: 'notifications' });
            };

            this.socket.onmessage = (event) => {
                if (this.enableLogging) {
                    console.log(`${this.SOCKET_TAG} Message received:`, event);
                }
                this.handleMessage(event.data);
            };

        } catch (error) {
            this.isConnecting = false;
            console.error(`${this.SOCKET_TAG} Failed to create WebSocket:`, error);

            // Reject the connection promise if it exists
            if (this.connectionReject) {
                this.connectionReject(error);
                this.connectionResolve = null;
                this.connectionReject = null;
            }
            if (this.connectionTimeout) {
                clearTimeout(this.connectionTimeout);
                this.connectionTimeout = null;
            }

            trackSocketError(error, TrackingEvents.socketConnectionError, { context: 'notifications' });
        }
    }

    private sendJoinRoom(userId: string): void {
        if (this.socket?.readyState === WebSocket.OPEN) {
            const joinMessage = {
                event: 'joinRoom',
                data: { userId },
                timestamp: Date.now()
            };

            this.socket.send(JSON.stringify(joinMessage));

            if (this.enableLogging) {
                console.log(`${this.SOCKET_TAG} Sent joinRoom for user ${userId}`);
            }
        }
    }

    private handleReconnection(userId: string): void {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

            if (this.enableLogging) {
                console.log(`${this.SOCKET_TAG} Attempting reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
            }

            setTimeout(() => {
                const url = `${utilService.getLiveHostURL()}/nsweb?userId=${userId}`;
                this.setupWebSocket(url, userId);
            }, delay);
        }
    }

    private handleMessage(data: string): void {
        try {
            const message = JSON.parse(data);

            if (this.enableLogging) {
                console.log(`${this.SOCKET_TAG} Received message:`, message);
            }

            // Handle ping/pong
            if (message.event === 'ping') {
                this.handlePing(message.data);
                return;
            }

            if (message.event === 'pong') {
                this.handlePong(message.data);
                return;
            }

            // Handle response to specific requests
            if (message.requestId && this.pendingRequests.has(message.requestId)) {
                const { resolve, reject, timeout } = this.pendingRequests.get(message.requestId)!;
                clearTimeout(timeout);
                this.pendingRequests.delete(message.requestId);

                if (message.error) {
                    reject(new Error(message.error));
                } else {
                    resolve(message.data);
                }
                return;
            }

            // Trigger event handlers
            const handlers = this.eventHandlers.get(message.event) || [];
            handlers.forEach(handler => handler(message));

        } catch (error) {
            console.error(`${this.SOCKET_TAG} Error parsing message:`, error, data);
        }
    }

    private handlePing(data: any): void {
        this.lastPongReceived = Date.now();
        if (this.enableLogging) {
            console.log(`${this.SOCKET_TAG} Received ping, sending pong`);
        }
        this.emit('pong', {
            timestamp: Date.now(),
            receivedPing: data
        });
    }

    private handlePong(data: any): void {
        this.lastPongReceived = Date.now();
        if (this.enableLogging) {
            console.log(`${this.SOCKET_TAG} Received pong:`, data);
        }
    }

    private setupHealthChecks(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }

        if (!this.isConnected) {
            return;
        }


        this.pingInterval = setInterval(() => {
            if (this.isConnected && this.socket?.readyState === WebSocket.OPEN) {
                // Check if connection is stale
                if (this.lastPongReceived > 0 && Date.now() - this.lastPongReceived > 60000) {
                    console.warn(`${this.SOCKET_TAG} No pong received in 60 seconds, reconnecting`);
                    this.cleanup();
                    return;
                }

                // Send ping
                if (this.enableLogging) {
                    console.log(`${this.SOCKET_TAG} Sending health check ping`);
                }
                this.emit('ping', { timestamp: Date.now() });
            } else {
                // Clear interval if not connected
                if (this.pingInterval) {
                    clearInterval(this.pingInterval);
                    this.pingInterval = null;
                }
            }
        }, DEFAULT_NOTIFICATIONS_PING_INTERVAL);
    }

    on<T = any>(event: string, handler: EventHandler<T>): this {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event)?.push(handler);
        return this;
    }

    off(event: string, handler?: EventHandler): this {
        const handlers = this.eventHandlers.get(event) || [];
        if (handler) {
            this.eventHandlers.set(
                event,
                handlers.filter(h => h !== handler)
            );
        } else {
            this.eventHandlers.delete(event);
        }
        return this;
    }

    emit(event: string, data?: any): this {
        if (!this.isConnected) {
            console.error(`${this.SOCKET_TAG} Cannot emit - socket not connected`);
            // Queue message for when connection is restored
            // this.messageQueue.push({ event, data });
            return this;
        }

        const message = {
            event,
            data,
            timestamp: Date.now()
        };

        this.socket?.send(JSON.stringify(message));

        if (this.enableLogging) {
            console.log(`${this.SOCKET_TAG} Emitted event: ${event}`, data);
        }

        return this;
    }

    emitWithCallback<T = any>(
        event: string,
        data: any,
        callback: AckCallback<T>
    ): this {
        const requestId = (this.requestId++).toString();
        const message = {
            event,
            data,
            requestId,
            timestamp: Date.now()
        };

        if (!this.isConnected) {
            console.error(`${this.SOCKET_TAG} Cannot emit - socket not connected`);
            callback(undefined, new Error('Socket not connected'));
            return this;
        }

        const timeout = setTimeout(() => {
            if (this.pendingRequests.has(requestId)) {
                this.pendingRequests.delete(requestId);
                callback(undefined, new Error(`Server did not respond within ${DEFAULT_ACK_TIMEOUT}ms`));
            }
        }, DEFAULT_ACK_TIMEOUT);

        this.pendingRequests.set(requestId, {
            resolve: (response: T) => callback(response, undefined),
            reject: (error: Error) => callback(undefined, error),
            timeout
        });

        this.socket?.send(JSON.stringify(message));

        if (this.enableLogging) {
            console.log(`${this.SOCKET_TAG} Emitted event with callback: ${event}`, data);
        }

        return this;
    }

    // private processMessageQueue(): void {
    //     while (this.messageQueue.length > 0 && this.isConnected) {
    //         const { event, data } = this.messageQueue.shift()!;
    //         this.emit(event, data);
    //     }
    // }
    // In NotificationSocketBuilder class
    connect(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isConnected) {
                resolve();
                return;
            }

            if (this.isConnecting) {
                // If already connecting, wait for that connection
                const checkConnection = setInterval(() => {
                    if (this.isConnected) {
                        clearInterval(checkConnection);
                        resolve();
                    } else if (!this.isConnecting) {
                        clearInterval(checkConnection);
                        reject(new Error('Previous connection attempt failed'));
                    }
                }, 100);
                return;
            }
            if (!this.socket) {
                reject(new Error('Socket not initialized. Call initialize() first.'));
                return;
            }

            // Store the resolve/reject functions to call them later
            this.connectionResolve = resolve;
            this.connectionReject = reject;

            // Set a timeout for connection
            this.connectionTimeout = setTimeout(() => {
                if (this.connectionReject) {
                    this.connectionReject(new Error('Connection timeout'));
                    this.connectionResolve = null;
                    this.connectionReject = null;
                }
            }, 10000); // 10 second timeout

        });
    }
    disconnect(): void {
        if (this.socket) {
            if (this.enableLogging) {
                console.log(`${this.SOCKET_TAG} Disconnecting...`);
            }
            this.socket.close(1000, 'Normal closure');
            this.isConnected = false;
        }
    }

    getConnectionState(): string {
        if (!this.socket) return 'not_initialized';
        if (this.isConnected) return 'connected';
        if (this.isConnecting) return 'connecting';
        return 'disconnected';
    }

    cleanup(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }

        if (this.socket) {
            this.socket.onopen = null;
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.onmessage = null;
            this.socket.close();
            this.socket = null;
        }

        // Clear pending requests
        this.pendingRequests.forEach(({ reject, timeout }) => {
            clearTimeout(timeout);
            reject(new Error('Connection closed'));
        });
        this.pendingRequests.clear();

        this.eventHandlers.clear();
        this.messageQueue = [];
        this.isConnected = false;
        this.isConnecting = false;
        this.lastPongReceived = 0;
        this.reconnectAttempts = 0;

        if (this.enableLogging) {
            console.log(`${this.SOCKET_TAG} Cleaned up`);
        }
    }
}

// Helper functions (keep the same)
const trackSocketError = (error: any, context: string, metadata?: Record<string, any>) => {
    trackError(error, {
        context: `NotificationSocket:${context}`,
        ...metadata
    });
};

const trackSocketEvent = (eventName: string, metadata?: Record<string, any>) => {
    trackEvent(eventName, metadata);
};

export default NotificationSocketBuilder;